import { z } from 'zod';

import type { PageSeo } from '@nucleus-commerce/api-client/admin';

import type { SeoOverrideValues } from './seo-preview';

const isAbsoluteUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
};

/** The `seo` override pages and blog posts share; empty strings mean "use the default". */
export const seoOverrideSchema = z.object({
  title: z.string().trim().max(200, { error: 'seo:validation.titleLength' }),
  description: z.string().trim().max(500, { error: 'seo:validation.descriptionLength' }),
  canonical: z
    .string()
    .trim()
    .max(500, { error: 'seo:validation.canonicalLength' })
    .refine((value) => value === '' || isAbsoluteUrl(value), {
      error: 'seo:validation.canonicalUrl',
    }),
  noIndex: z.boolean(),
});

export type SeoOverrideForm = z.infer<typeof seoOverrideSchema>;

export function emptySeoOverride(): SeoOverrideValues {
  return { title: '', description: '', canonical: '', noIndex: false };
}

export function fromSeoOverride(seo: PageSeo | undefined): SeoOverrideValues {
  return {
    title: seo?.title ?? '',
    description: seo?.description ?? '',
    canonical: seo?.canonical ?? '',
    noIndex: seo?.noIndex ?? false,
  };
}

/** Null clears the override entirely, which is what an untouched section means. */
export function toSeoOverride(values: SeoOverrideValues): NonNullable<PageSeo> | null {
  const title = values.title.trim();
  const description = values.description.trim();
  const canonical = values.canonical.trim();
  if (!title && !description && !canonical && !values.noIndex) return null;
  return {
    title: title || null,
    description: description || null,
    canonical: canonical || null,
    noIndex: values.noIndex,
  };
}
